import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";

const ProjectionHelpDialog = ({
  open,
  onClose,
  title = "Auswahlmöglichkeiten bei den Zukunftszeiträumen",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="projection-help-title"
    >
      <DialogTitle sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "2px solid #d3d3d3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
            }}
            aria-hidden
          >
            <QuestionMarkIcon
              sx={(theme) => ({ color: theme.palette.brand?.red })}
            />
          </Box>
          <Typography id="projection-help-title" sx={{ fontWeight: "bold" }}>
            {title}
          </Typography>
        </Box>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.brand?.red,
          })}
          size="large"
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Stack>
            <Typography
              variant="subtitle2"
              sx={{ textDecoration: "underline" }}
            >
              RCP8.5 und RCP2.6
            </Typography>
            <Typography variant="body2">
              Um das Klima der Zukunft zu projizieren, werden in der
              Klimaforschung Szenarien möglicher Treibhausgasemissionen
              herangezogen. Die verwendeten Szenarien RCP8.5 und RCP2.6 stehen
              für einen zusätzlichen Strahlungsantrieb durch anthropogene
              Treibhausgase von 8,5 W/m2, bzw. 2,6 W/m2. Das RCP8.5-Szenario,
              genannt „Kein-Klimaschutz“-Szenario, repräsentiert eine Zukunft
              ohne nennenswerten Klimaschutz, in der vorwiegend auf fossile
              Energieträger zur Energieerzeugung gesetzt wird. Demgegenüber
              steht das RCP2.6-Szenario, genannt „Klimaschutz“-Szenario, das
              eine Zukunft repräsentiert, in der erhebliche Anstrengungen zur
              Reduktion von Treibhausgasemissionen und -konzentrationen auf
              globaler Ebene umgesetzt werden und die globale Erwärmung auf
              unter 2 °C gegenüber dem vorindustriellen Niveau begrenzt wird.
            </Typography>
          </Stack>

          <Stack>
            <Typography
              variant="subtitle2"
              sx={{ textDecoration: "underline" }}
            >
              Maximum, Mittel und Minimum
            </Typography>
            <Typography variant="body2">
              Standard in der Klimaforschung ist die Betrachtung von
              Klimamodellensembles, in denen eine Vielzahl von Klimamodellen
              ausgewertet werden, um die klimatische Entwicklung im Zuge eines
              Szenarios abzuschätzen. Hintergrund ist, dass jedes Modell nur
              eine vereinfachte Abbildung der Realität darstellt und diese mit
              z. T. unterschiedlichen mathematischen Ansätzen berechnet. Dabei
              sind alle Ergebnisse verschiedener Modelle gleich wahrscheinlich.
              Diese Vielfalt an Ergebnissen innerhalb eines Klimamodellensembles
              führt zu einer Ergebnis-Bandbreite, innerhalb derer sich die
              zukünftige Entwicklung der klimatischen Verhältnisse abspielen
              kann. Das RCP8.5-Szenario beinhaltet 11 Klimamodelle und das
              RCP2.6 beinhaltet 8 Klimamodelle. Mit der Auswahl Minimum erhalten
              Sie den unteren Rand der Ergebnisbandbreite, mit Maximum den
              oberen Rand der Ergebnisbandbreite und mit Mittel den Mittelwert
              der Ergebnisbandbreite.
            </Typography>
          </Stack>

          <Stack>
            <Typography
              variant="subtitle2"
              sx={{ textDecoration: "underline" }}
            >
              Änderung zu 1971-2000
            </Typography>
            <Typography variant="body2">
              Änderungen der Zukunft zur Vergangenheit werden für die
              Klimamodelle immer auf den Zeitraum 1971-2000 bezogen, da bei
              einigen Klimamodellen die Simulation erst 1971 startet.
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={(theme) => ({
              fontStyle: "italic",
              color: theme.palette.brand?.blue,
              "& a": {
                color: theme.palette.brand?.blue,
                fontStyle: "italic",
              },
            })}
          >
            Ausführliche Hintergrundinformationen zu den verwendeten
            Klimamodellen finden Sie in{" "}
            <a
              href="https://nibis.lbeg.de/DOI/dateien/Geofakten_39_Text_4_2022.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Geofakten 39.
            </a>
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectionHelpDialog;
